import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { skipWhile, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import * as OrderApproveActions from '../../actions/order-approve.actions';
import { AnalyticsService } from '../../analytics/analytics.service';
import { PageTypes } from '../../analytics/enums/pageTypes';
import { CheckoutFacade } from '../../facades/checkout.facade';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { CustomerFacade } from '../../facades/customer.facade';
import { MarketingFacade } from '../../facades/marketing.facade';
import { OrdersFacade } from '../../facades/orders.facade';
import { ICart, ICartRule } from '../../models/cart.models';
import {
  IApproverData,
  ICheckout,
  IPaymentMethodName,
  IPaymentProviderName,
  IShipmentMethod,
} from '../../models/checkout.models';
import { IAddress, ISelectBoxOption } from '../../models/common.models';
import { ICustomer } from '../../models/customer.models';
import { State } from '../../reducers';
import { I18nService } from '../../services';
import { TermConstants } from '../../shared/terms/TermConstants';
import { AddressUtils } from '../../utils/address.utils';
import { ContactPointModalComponent } from '../../shared/contact-point-modal/contact-point-modal.component';
import { AppUtils } from '../../utils/app.utils';
import { EStoreTypes, EUserRoles } from '../../configurations/common';
import { environment } from '../../../environments/environment';
import { TaxUtils } from '../../utils/tax.utils';

@Component({
  selector: 'app-order-approve',
  templateUrl: './order-approve.component.html',
  styleUrls: ['./order-approve.component.scss'],
})
export class OrderApproveComponent implements OnInit, OnDestroy {
  @Input() modelValue: any;
  @ViewChild(ContactPointModalComponent) pocModal: ContactPointModalComponent;

  isAuStore: boolean = false;
  isJpStore: boolean = false;

  showModalContactPoint: boolean;
  showModalChangeAddress: boolean;
  showModalConfirmRemove: boolean;
  showModalDeclineConfirm: boolean;
  showSuccessModal: boolean = false;

  addresses: Array<ISelectBoxOption> = [];
  businessAddressesLoaded: boolean;
  businessAddresses: any;

  selectedDeliveryAddress: any;
  selectedDeliveryAddressLoaded: boolean = false;

  billingAddress: IAddress;
  shippingAddress: IAddress;
  data: {data: ICheckout};

  currentCart: any;
  cartLoaded: boolean;
  itemsInCart: any[];
  cartTotals: any;
  cartId: string;
  clickedItemDataName: string;
  clickedItemDataId: string;

  termsAccessTime: Date;
  privacyPolicyUrl = {
    value: '', type: 'external',
  };
  termsAndConditionsUrl = {
    value: '', type: 'internal',
  };
  approveOrderForm: any;

  voucherButtonIsClicked: boolean;
  voucherHasError: boolean = false;
  voucherList = [];
  showAdditionalInfoForVoucher: boolean;
  voucherListLoaded: boolean = false;
  totalPrice: number;
  cartSubtotal: number;
  cartApprovalData: any;
  checkoutData: ICheckout;
  loggedUserRoles: EUserRoles[];
  approverInfo: {
    approverId: string,
    comment: string,
    dueDate: string,
    firstName: string,
    lastName: string
  };
  cartCurrency: string;
  currentUserCartId: string;
  cartApprovalDataLoaded: boolean = false;
  actionInProgress: boolean = false;
  pendingOrder: any;
  deleteLastItemAndDeclineOrderFlag: boolean;
  cartItemsCount: number;
  isGuest: boolean;
  loggedUserId: string;
  userData: ICustomer;
  cartRules: Array<ICartRule>;
  voucherInProgress: boolean = false;
  selectedShipmentMethod: IShipmentMethod;
  shipmentMethodsLoaded: boolean = false;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private checkoutFacade: CheckoutFacade,
    private store: Store<State>,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private analyticsService: AnalyticsService,
    private marketingFacade: MarketingFacade,
    private ordersFacade: OrdersFacade,
    private customerFacade: CustomerFacade,
    private configurationFacade: ConfigurationFacade,
    private i18nService: I18nService,
  ) {
  }

  ngOnInit(): void {
    this.isAuStore = AppUtils.isStoreActive(EStoreTypes.AU);
    this.isJpStore = AppUtils.isStoreActive(EStoreTypes.JP);

    this.route.params.subscribe((params) => {
      if (params.orderId) {
        this.cartId = params.orderId;
      }
    });
    this.getCartApprovalData();
    this.customerFacade.beginGetBusinessUnitsAction();
    this.selectBusinessAddresses();
    this.initializeForm();

    this.currentUserCartId = '';
    this.getMiniCartData();

    this.getCompanyUserData();
    this.getUserData();

    this.privacyPolicyUrl = this.configurationFacade.getTermsUrl(
      TermConstants.termKeys.PRIVACY_POLICY.termsKey,
    );
    this.termsAndConditionsUrl = this.configurationFacade.getTermsUrl(
      TermConstants.termKeys.TERMS_AND_CONDITIONS.termsKey,
    );

    this.fetchSelectedShipmentMethod();
  }

  ngOnDestroy(): void {
    this.clearCheckoutData();

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getMiniCartData(): void {
    this.marketingFacade.selectCartId().pipe(
      takeUntil(this.unsubscribe$),
      skipWhile(cartId => !cartId),
    ).subscribe(cartId => {
      this.currentUserCartId = cartId;

      if (cartId !== this.cartId) {
        this.checkoutFacade.actionLoadCartItemsOrderApprove(this.cartId);
      }

      this.selectCartItemsSubscription();
      this.selectCartItemsLoadFailSubscription();
    });
  }

  selectCartItemsLoadFailSubscription(): void {
    this.checkoutFacade.selectCartItemsLoadFail().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cartItemsLoadFail => {
      if (cartItemsLoadFail) {
        this.router.navigate([this.i18nService.getCurrentLocale()]).then();
      }
    });
  }

  selectCartItemsSubscription(): void {
    const cartItems$ = this.cartId === this.currentUserCartId ?
      this.marketingFacade.getCurrentCartItems() :
      this.checkoutFacade.selectCartItemsOrderApprove();

    cartItems$.pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cartItems => {
      if (!cartItems) {
        return;
      }

      if (cartItems) {
        let cart: ICart;
        cart = cartItems as ICart;
        this.currentCart = cart;
        this.cartRules = cartItems.included.filter(include => include.type === 'cart-rules');
        this.analyticsService.setProducts(this.currentCart);
        this.analyticsService.setCartId(this.cartId);
        this.itemsInCart = this.currentCart.included?.filter(
          (result) => result.type === 'items',
        );
        this.voucherList = this.currentCart.included?.filter(
          result => result.type === 'vouchers',
        );
        if (this.voucherList.length > 0) {
          this.showAdditionalInfoForVoucher = true;
        }
        this.cartLoaded = true;
        this.cartTotals = this.getTotals(this.currentCart);
        this.cartCurrency = this.getCurrency(this.currentCart);
        this.cartItemsCount = this.getCartItemsCount(this.currentCart);
        this.analyticsService.trackOrderApproved('Order Placed', PageTypes.ORDER_CONFIRMATION_PAGE);
      }
    });
  }

  initializeForm(): void {
    this.approveOrderForm = this.formBuilder.group({
      address: ['', [Validators.required]],
      invoiceEmailCopy: ['', [Validators.email]],
      paymentMethod: 'Invoice to Hospital',
      promotionCode: '',
      deliveryMethod: 'Normal delivery',
      termsAndConditionsConsent: false,
      privacyPolicyConsent: false,
      taxNumber: ['', Validators.maxLength(30)],
    });
  }

  selectBusinessAddresses(): void {
    this.customerFacade.selectBusinessAddress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(addresses => {
      if (addresses) {
        this.businessAddresses = addresses;
        AddressUtils.appendAddresses(this.addresses, this.businessAddresses, 'business');
        this.businessAddressesLoaded = true;
      }
    });
  }

  showModalToChangeAddress(): void {
    this.showModalChangeAddress = !this.showModalChangeAddress;
  }

  showModalToContactPoint(): void {
    this.showModalContactPoint = !this.showModalContactPoint;
  }

  clickPromotionalButton(): void {
    this.voucherButtonIsClicked = true;
  }

  formIsValid(): boolean {
    return this.approveOrderForm.status === 'VALID';
  }

  placeOrder(): void {
    if (this.formIsValid()) {
      const {value} = this.approveOrderForm.value.address;
      this.billingAddress = {
        idCustomerAddress: value.idCustomerAddress,
        idCompanyUnitAddress: value.idCompanyUnitAddress,
        salutation: 'Mr',
        firstName: 'empty',
        lastName: 'empty',
        address1: (value.address1) ? value.address1 : 'empty',
        address2: (value.address2) ? value.address2 : 'empty',
        zipCode: (value.zipCode) ? value.zipCode : 'empty',
        city: (value.city) ? value.city : 'empty',
        iso2Code: (value.iso2Code) ? value.iso2Code : 'empty',
        phone: (value.phone) ? value.phone : 'empty',
        company: 'empty',
        country: (value.country) ? value.country : 'empty',
        isDefaultBilling: false,
        isDefaultShipping: false,
      };

      const invoiceEmailCopyFormatted = this.approveOrderForm.value.invoiceEmailCopy.length > 0 ?
        this.approveOrderForm.value.invoiceEmailCopy : undefined;

      this.checkoutData = {
        type: 'checkout',
        attributes: {
          idCart: this.cartId,
          isAddressSavingSkipped: true,
          taxNumber: this.approveOrderForm.value.taxNumber,
          pointOfContact: this.cartApprovalData.attributes.pointOfContact,
          approverDetails: this.prepareApproverData(this.cartApprovalData.attributes.approverDetails.comment,
            this.cartApprovalData.attributes.approverDetails.dueDate),
          customer: this.cartApprovalData.attributes.customer,
          billingAddress: this.billingAddress,
          shippingAddress: this.cartApprovalData.attributes.shippingAddress,
          payments: [{
            paymentMethodName: IPaymentMethodName.DUMMY,
            paymentProviderName: IPaymentProviderName.DUMMY,
          }],
          shipment: {
            idShipmentMethod: this.selectedShipmentMethod?.id ?? 1,
          },
          // both checkboxes need to be checked for the form to be valid, which should be the case here
          termsAccessTime: this.termsAccessTime,
          consentedTerms: [
            ...(this.approveOrderForm.controls.termsAndConditionsConsent.value ?
              [TermConstants.termKeys.TERMS_AND_CONDITIONS.termsKey] : []),
            ...(this.approveOrderForm.controls.privacyPolicyConsent.value ?
              [TermConstants.termKeys.PRIVACY_POLICY.termsKey] : []),
          ],
          invoiceEmailCopy: invoiceEmailCopyFormatted,
        },
      };
      this.data = {
        data: this.checkoutData,
      };

      this.actionInProgress = true;
      this.checkoutFacade.postCheckout(this.data).pipe(
        takeUntil(this.unsubscribe$),
      ).subscribe({
          next: response => {
            this.showSuccessModal = true;
            this.analyticsService.trackCart('order.approved');
            this.store.dispatch(OrderApproveActions.postCheckoutDataSuccess({postCheckoutDataResponse: response}));
            this.store.dispatch(OrderApproveActions.setPostCheckoutItems({items: this.currentCart}));
            this.store.dispatch(OrderApproveActions.setUserCartIdDuringCheckout({cartId: this.currentUserCartId}));

            // clear cart after checkout when it is your cart
            if (this.cartId === this.currentUserCartId) {
              this.marketingFacade.createEmptyCart();
            }
            this.proceedToSuccessPage();
            this.actionInProgress = false;
          },
          error: (error) => {
            this.handleCheckoutError(error);
            this.actionInProgress = false;
          },
        },
      );
    }
  }

  prepareApproverData(comment: string, dueDate: string): IApproverData {
    return {
      approverId: this.loggedUserId,
      comment,
      dueDate,
      firstName: this.userData.firstName,
      lastName: this.userData.lastName,
    };
  }

  getItemPicture(id: string): string {
    const idForInfo = id['concrete-products'].data[0].id;
    return this.currentCart.included?.filter((tmp) => tmp.type === 'concrete-product-image-sets' && tmp.id === idForInfo)[0]
      .attributes.imageSets.find((img) => img.name === 'default')
      .images.find((smallImage) => smallImage.externalUrlSmall)
      .externalUrlSmall;
  }

  getTotals(cart: any): any {
    return cart.data.attributes.totals;
  }

  getCurrency(cart: any): any {
    return cart.data.attributes.currency;
  }

  getItemPrice(id: string): number {
    return this.currentCart.included?.filter((tmp) => tmp.type === 'items' && tmp.id === id)[0].attributes.calculations.unitPrice;
  }

  getItemName(id: string): string {
    const idForInfo = id['concrete-products'].data[0].id;
    return this.currentCart.included?.filter((tmp) => tmp.type === 'concrete-products' && tmp.id === idForInfo)[0].attributes.name;
  }

  getItemSku(id: string): string {
    const idForInfo = id['concrete-products'].data[0].id;
    return this.currentCart.included?.filter((tmp) => tmp.type === 'concrete-products' && tmp.id === idForInfo)[0].id;
  }

  getVoucherTitle(id: string): number {
    const voucherDiscount = this.voucherList.filter(tmp => tmp.type === 'vouchers' && tmp.id === id)[0].attributes.amount;
    return Math.round((voucherDiscount * 100) / this.cartSubtotal);
  }

  getVoucherAmount(id: string): number {
    return this.voucherList.filter(tmp => tmp.type === 'vouchers' && tmp.id === id)[0].attributes.amount;
  }

  getTotalPrice(cartTotals: any): number {
    this.cartSubtotal = cartTotals.subtotal;
    if (this.isJpStore) {
      this.totalPrice = cartTotals.subtotal;
    } else {
      this.totalPrice = cartTotals.taxTotal + cartTotals.subtotal;
    }
    return this.totalPrice;
  }

  getPriceAfterVoucher(): number {
    let totalAmount = 0;
    if (this.voucherList.length > 0) {
      this.voucherList.forEach(
        tmp => {
          totalAmount += tmp.attributes.amount;
        });
    }
    return this.totalPrice - totalAmount;
  }

  getSubTotal(): number {
    return this.cartTotals.subtotal ?? 0;
  }

  getGrandTotal(): number {
    return this.cartTotals.grandTotal ?? 0;
  }

  getShipmentTotal(): number {
    return this.cartTotals.expenseTotal ?? 0;
  }

  getTaxTotal(): number {
    return this.cartTotals.taxTotal ?? 0;
  }

  getTaxPercent(): number {
    return TaxUtils.getTaxPercentage(this.itemsInCart);
  }

  removeItemFromCart(): void {
    this.store.dispatch(
      OrderApproveActions.deleteItemFromCart({cartId: this.cartId, itemId: this.clickedItemDataId}),
    );
    this.showModalConfirmRemove = false;
  }

  checkAllLoaded(): boolean {
    return this.cartApprovalDataLoaded && this.cartLoaded && this.shipmentMethodsLoaded;
  }

  getCartApprovalData(): void {
    this.ordersFacade.getCartForApprovalData(this.cartId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(response => {
        this.cartApprovalData = response.data;
        this.cartApprovalDataLoaded = true;
        this.selectedDeliveryAddress = this.cartApprovalData.attributes.shippingAddress;
        this.store.dispatch(OrderApproveActions.setShippingAddress({address: this.selectedDeliveryAddress}));
        this.approverInfo = this.cartApprovalData.attributes.approverDetails;
        this.selectedDeliveryAddressLoaded = true;

        this.pocModal.pocData = this.cartApprovalData.attributes.pointOfContact;
        this.pocModal.initializeForm();
      });
  }

  setOrderFormValue(event: any): void {
    this.approveOrderForm.patchValue({
      [event.key]: event.value,
    });
  }

  setAddress(event: any): void {
    this.approveOrderForm.patchValue({
      [event.key]: {name: event.name, value: event.value},
    });
  }

  restartShowModal(): void {
    this.showModalConfirmRemove = false;
    this.deleteLastItemAndDeclineOrderFlag = false;
  }

  removeItemFromCartModal(itemName: any, id: any): void {
    this.clickedItemDataName = itemName;
    this.clickedItemDataId = id;
    this.showModalConfirmRemove = true;
    this.deleteLastItemAndDeclineOrderFlag = this.cartItemsCount <= 1;
  }

  proceedToSuccessPage(): void {
    this.checkoutFacade.getResponseCheckoutData().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(
      response => {
        if (response?.data) {
          this.router.navigateByUrl('/summary-success/' + response.data.id);
        }
      },
    );
  }

  backToOrders(): void {
    this.router.navigate(['/my-orders']);
  }

  addVoucher(): void {
    this.voucherInProgress = true;
    this.marketingFacade.postVoucher(this.cartId, this.approveOrderForm.value.promotionCode).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe({
      next: response => {
        if (response) {
          const currentCartCopy = {...this.currentCart};
          currentCartCopy.data = response.data;
          this.currentCart = currentCartCopy;
          this.voucherList = response.included;
          this.voucherListLoaded = true;
          this.voucherHasError = false;
          this.showAdditionalInfoForVoucher = true;
          this.setOrderFormValue({key: 'promotionCode', value: ''});
        }
        this.voucherInProgress = false;
      },
      error: () => {
        this.voucherListLoaded = false;
        this.voucherHasError = true;
        this.voucherInProgress = false;
      },
    });
  }

  removeVoucher(voucherId: string): void {
    this.actionInProgress = true;
    this.marketingFacade.removeVoucher(this.cartId, voucherId).then(
      response => {
        this.actionInProgress = false;
        if (response && response instanceof HttpErrorResponse) {
          this.voucherHasError = true;
        } else {
          this.setOrderFormValue({key: 'promotionCode', value: ''});
          this.voucherListLoaded = false;
          this.voucherList = this.voucherList.filter(voucher => voucher.attributes.code !== voucherId);
        }

        if (this.voucherList === undefined || this.voucherList.length === 0) {
          this.showAdditionalInfoForVoucher = false;
        }

      },
    );
  }

  selectNewAddress(): void {
    this.getCartApprovalData();
  }

  getCartItemsCount(cart: any): number {
    let itemsCount = 0;
    if (cart.included.length > 0) {
      cart.included.forEach(
        item => {
          if (item.type === 'items') {
            itemsCount++;
          }
        },
      );
    }
    if (itemsCount === 1) {
      this.deleteLastItemAndDeclineOrderFlag = true;
    }
    return itemsCount;
  }

  lastItemDeclineOrder(): void {
    this.pendingOrder = {
      data: {
        type: 'quote-decline',
        attributes: {
          idCart: this.cartId,
          approverDetails: {
            approverId: this.cartApprovalData.attributes.approverDetails.approverId,
            approverFirstName: this.cartApprovalData.attributes.approverDetails.firstName,
            approverLastName: this.cartApprovalData.attributes.approverDetails.lastName,
            comment: this.cartApprovalData.attributes.approverDetails.comment,
            dueDate: this.cartApprovalData.attributes.approverDetails.dueDate,
          },
          customer: {
            email: this.cartApprovalData.attributes.customer.email,
            salutation: this.cartApprovalData.attributes.customer.salutation,
            firstName: this.cartApprovalData.attributes.customer.firstName,
            lastName: this.cartApprovalData.attributes.customer.lastName,
          },
        },
      },
    };
    this.ordersFacade.postDeclinePendingOrder(this.pendingOrder).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(() => {
      this.showModalConfirmRemove = false;
      if (this.cartId === this.currentUserCartId) {
        this.marketingFacade.createEmptyCart();
      }
      this.ordersFacade.loadCarts();

      this.router.navigate([this.i18nService.getCurrentLocale()]);
    });
  }

  getCompanyUserData(): void {
    this.customerFacade.getMyOrderPageData().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.isGuest = state.isGuest;
      this.loggedUserRoles = state.companyRoles;
      this.loggedUserId = state.id;
    });
  }

  getUserData(): void {
    this.customerFacade.selectMyProfileInformation().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.userData = state;
      this.termsAccessTime = new Date();
    });
  }

  clearCheckoutData(): void {
    this.store.dispatch(OrderApproveActions.getCheckoutUpdateDataSuccess({checkoutUpdateData: null}));
    this.store.dispatch(OrderApproveActions.setShippingAddress({address: null}));

    this.marketingFacade.selectCartId().pipe(
      take(1),
    ).subscribe(cartId => {
      this.marketingFacade.updateCartById(cartId);
    });
  }

  private handleCheckoutError(httpError: HttpErrorResponse): void {
    if (httpError.status === 422) {
      // unprocessable entity/validation error
      // 1. reset previous alert to type warning
      this.configurationFacade.getAlert().pipe(
        take(1),
      ).subscribe((alert) => {
        if (alert && alert.type === 'error' && alert.message) {
          // change the alert type for the user to 'warning'
          this.configurationFacade.setAlert({
            ...alert,
            type: 'warning',
          });
        }
      });
      // 2. reset terms and conditions consent
      // NOTE: Currently not possible to distinguish terms and conditions change from other validation errors
      // without checking a localization-specific attribute but... at present this is the only type of validation
      // error that the BE can throw on checkout, so we can assume it's that
      this.handleTermsAndConditionsChanged();
    }
  }

  private handleTermsAndConditionsChanged(): void {
    // reset terms access time as the user is now aware of term changes (and uncheck prior consent)
    this.termsAccessTime = new Date();
    this.approveOrderForm.controls.termsAndConditionsConsent.setValue(false);
    // that's it: the shared error handling will display the appropriate message from the BE
  }

  isVoucherBtnDisabled(): boolean {
    return this.voucherInProgress || this.approveOrderForm.value.promotionCode === '';
  }

  resetVoucherField(): void {
    this.voucherListLoaded = false;
    this.voucherHasError = false;
  }

  isExcludeTaxActive(): boolean {
    return environment.features.excludeTax.includes(this.getCurrentStore());
  }

  getCurrentStore(): string {
    return AppUtils.getCurrentStore().storeId;
  }

  private fetchSelectedShipmentMethod(): void {
    const data = {
      type: 'checkout-data',
      attributes: {
        idCart: this.cartId,
      },
    };

    this.checkoutFacade.getCheckoutData({data: data})
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(response => {
        if (response?.included) {
          const shipmentMethod = response.included?.filter(include => include.type === 'shipments')[0];
          this.selectedShipmentMethod = shipmentMethod.attributes?.selectedShipmentMethod;
          this.shipmentMethodsLoaded = true;
        }
      });
  }

  isJapanStore(): boolean {
    return this.getCurrentStore() === 'JP';
  }
}
